import {captureException} from '@sentry/browser';
import {
  DataStatus,
  FormControl,
  FormField,
  OptionType,
  showNotification,
} from 'platform/components';
import {Grid} from 'platform/foundation';

import {useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNilOrEmpty, isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {useLazyCustomerAdvancedSearchQuery, useLazyGetCustomerV2Query} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {getCustomerName} from '../../../utils/getCustomerName';
import {getAdvancedCustomerName} from '../../SendCustomerEmailForm/utils/getAdvancedCustomerName';
import {FormValues} from '../types/FormValues';
import {getAdvancedCustomerPhoneNumbers} from '../utils/getAdvancedCustomerPhoneNumbers';
import {getCustomerPhoneNumbers} from '../utils/getCustomerPhoneNumbers';

interface CustomerSelectChoiceProps extends TestIdProps {
  control: FormControl<FormValues>;
  formApi: UseFormReturn<FormValues>;
  defaultOptions?: OptionType[];
}

export function CustomerSelectField(props: CustomerSelectChoiceProps) {
  const [getCustomers, {data: loadedCustomers}] = useLazyCustomerAdvancedSearchQuery();
  const [getCustomer] = useLazyGetCustomerV2Query();

  const [isLoading, startLoading, stopLoading] = useBoolean(false);

  const [customerPhoneOptions, setCustomerPhoneOptions] = useState<OptionType[]>([]);
  const defaultOptions = props.defaultOptions ?? [];

  return (
    <DataStatus minHeight={14}>
      <Grid columns={[1, 2]}>
        <FormField
          control={props.control}
          name="customerId"
          type="asyncChoice"
          tooltip={
            isNotNilOrEmpty(defaultOptions)
              ? i18n.t('entity.customer.labels.defaultCustomersTooltip')
              : undefined
          }
          label={i18n.t('entity.customer.labels.customer')}
          noOptionsMessage={() => i18n.t('entity.customer.labels.typeToSearch')}
          isRequired
          isLoading={isLoading}
          loadLabels={async (customerId) => {
            if (!isString(customerId)) {
              setCustomerPhoneOptions([]);
              return [];
            }

            startLoading();

            const alreadyLoadedCustomer = loadedCustomers?.find(
              (customer) => customer.id === customerId
            );

            if (alreadyLoadedCustomer) {
              const customerPhoneNumber = getAdvancedCustomerPhoneNumbers(alreadyLoadedCustomer);
              setCustomerPhoneOptions(customerPhoneNumber);
              props.formApi.setValue('phoneNumber', customerPhoneNumber[0]?.value);
              stopLoading();
              return [{value: customerId, label: getAdvancedCustomerName(alreadyLoadedCustomer)}];
            }

            try {
              const customer = await getCustomer({customerId}).unwrap();
              const customerPhoneNumbers = getCustomerPhoneNumbers(customer);
              props.formApi.setValue('phoneNumber', customerPhoneNumbers[0]?.value);

              setCustomerPhoneOptions(customerPhoneNumbers);
              stopLoading();
              return [{value: customerId, label: getCustomerName(customer)}];
            } catch (error) {
              captureException(error);
              setCustomerPhoneOptions([]);
              showNotification.error();
              stopLoading();
              return [];
            }
          }}
          loadOptions={async (name) => {
            if (isNilOrEmpty(name) || !isString(name)) {
              return defaultOptions;
            }

            startLoading();

            try {
              const customers = await getCustomers({name}).unwrap();

              stopLoading();
              if (isNotNilOrEmpty(defaultOptions)) {
                return [
                  {
                    label: i18n.t('entity.serviceOrder.labels.defaultCustomers'),
                    options: defaultOptions,
                  },
                  {
                    label: i18n.t('entity.customer.labels.searchedCustomers'),
                    options: customers.map((customer) => ({
                      label: getAdvancedCustomerName(customer),
                      value: customer.id,
                    })),
                  },
                ];
              }

              return customers.map((customer) => ({
                label: getAdvancedCustomerName(customer),
                value: customer.id,
              }));
            } catch (error) {
              captureException(error);
              showNotification.error();
              stopLoading();
              return defaultOptions;
            }
          }}
          isNotClearable
          data-testid={suffixTestId('customerSelectField', props)}
        />

        <FormField
          control={props.control}
          name="phoneNumber"
          type="creatableChoice"
          label={i18n.t('general.labels.phone')}
          isRequired
          isLoading={isLoading}
          isNotClearable
          onChange={() => props.formApi.clearErrors('phoneNumber')}
          options={customerPhoneOptions}
          onCreateOption={(value) => {
            if (!isValidPhoneNumber(value)) {
              props.formApi.setError('phoneNumber', {
                message: i18n.t('general.validations.invalidPhoneNumber'),
              });
              return;
            }

            setCustomerPhoneOptions((prevState) => {
              if (prevState.some((option) => option.value === value)) {
                return prevState;
              }

              return [...prevState, {label: value, value}];
            });
            props.formApi.setValue('phoneNumber', value);
          }}
          data-testid={suffixTestId('phoneNumberSelectField', props)}
        />
      </Grid>
    </DataStatus>
  );
}

const isValidPhoneNumber = (value: string) => {
  const isValid = /^[\d\s+]+$/.test(value);

  return isValid;
};
